import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Row, Col, Button, message, Tabs, Table, Modal, Input, InputNumber, Select, Tag, Space, Popconfirm, Empty, Tooltip, DatePicker, Upload, Alert, Checkbox,
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, SwapOutlined, BankOutlined, WalletOutlined, AlipayCircleOutlined,
  DollarOutlined, ArrowUpOutlined, ArrowDownOutlined, FileDoneOutlined, SettingOutlined, ShopOutlined, SearchOutlined,
  ImportOutlined, InboxOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiFetch, notifyDataChanged } from '../utils/api.js';

const INK = '#26201C';
const CORAL = '#D85A30';
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

const fmtYuan = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '¥0';
  const neg = n < 0;
  const abs = Math.abs(n);
  const s = abs >= 10000 ? (abs / 10000).toFixed(2) + '万' : abs.toLocaleString('zh-CN', { maximumFractionDigits: 2 });
  return (neg ? '-¥' : '¥') + s;
};

const TYPE_META = {
  bank: { label: '银行卡', color: '#D85A30', icon: <BankOutlined /> },
  wechat: { label: '微信', color: '#3F8600', icon: <WalletOutlined /> },
  alipay: { label: '支付宝', color: '#5B8FF9', icon: <AlipayCircleOutlined /> },
  cash: { label: '现金', color: '#F5A623', icon: <DollarOutlined /> },
};

// 账户类型单字（玻璃卡片统一珊瑚色图标里的字）
const TYPE_CHAR = { bank: '银', wechat: '微', alipay: '支', cash: '现' };

// 完整精确数字（横幅大数字用，不转「万」）
const fmtFull = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '0.00';
  const neg = n < 0;
  const abs = Math.abs(n).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (neg ? '-' : '') + abs;
};

const today = () => dayjs().format('YYYY-MM-DD');

export default function CashAccount() {
  const [loading, setLoading] = useState(true);
  const [cash, setCash] = useState(null); // { accounts, shops, total, types, expense_accounts, income_accounts, reimbursement_pending, reimbursement_pending_amount }
  const [flow, setFlow] = useState([]);
  const [reimb, setReimb] = useState([]);
  const [tab, setTab] = useState('overview');
  const [waybill, setWaybill] = useState(null); // 面单余额核对数据

  // 应付账期关联（收款方供应商 + 未付应付下拉）
  const [suppliers, setSuppliers] = useState([]);
  const [unpaidPayables, setUnpaidPayables] = useState([]);

  // 流水筛选（草稿值；点「查找」后才应用到表格）
  const [fAccount, setFAccount] = useState('');
  const [fStart, setFStart] = useState('');
  const [fEnd, setFEnd] = useState('');
  const [fKey, setFKey] = useState('');
  const [applied, setApplied] = useState(null); // { account, start, end, key }

  // 弹窗开关
  const [openIncome, setOpenIncome] = useState(false);
  const [openExpense, setOpenExpense] = useState(false);
  const [openTransfer, setOpenTransfer] = useState(false);
  const [openReimb, setOpenReimb] = useState(false);
  const [openAccount, setOpenAccount] = useState(false);
  const [editAccount, setEditAccount] = useState(null);

  // ===== 导入账单 =====
  const [openImport, setOpenImport] = useState(false);
  const [imp, setImp] = useState({ account_id: '', file: null, loading: false, importing: false, preview: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, f, r, p, w] = await Promise.all([
        apiFetch('/api/cash-account').then(x => x.json()),
        apiFetch('/api/cash/flow').then(x => x.json()),
        apiFetch('/api/reimbursement').then(x => x.json()),
        apiFetch('/api/payable').then(x => x.json()).catch(() => ({ success: true, data: [], suppliers: [] })),
        apiFetch('/api/waybill-balance').then(x => x.json()).catch(() => ({ success: false })),
      ]);
      if (c.success) setCash(c);
      else message.error(c.error || '账户加载失败');
      if (f.success) setFlow(f.data || []);
      if (r.success) setReimb(r.data || []);
      if (w && w.success) setWaybill(w.data);
      if (p) {
        setSuppliers(p.suppliers || []);
        setUnpaidPayables((p.data || []).filter(x => String(x.status) !== '已付' && Number(x.remaining) > 0));
      }
    } catch (e) { message.error('资金中心加载失败：' + (e.message || '')); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // 记收入 / 记支出 / 转账 / 报销 / 账户 通用提交后刷新
  const afterMutate = (msg) => { message.success(msg); notifyDataChanged(); load(); };

  // ===== 记收入 =====
  const [inc, setInc] = useState({ account_id: '', account_code: '4001', amount: '', date: today(), summary: '' });
  const submitIncome = async () => {
    if (!inc.account_id) return message.warning('请选择收款账户');
    if (!inc.amount || Number(inc.amount) <= 0) return message.warning('请填写正数金额');
    try {
      const res = await apiFetch('/api/ledger/flow', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ biz_date: inc.date, source: '手工', entity_id: 1, account_code: inc.account_code, summary: inc.summary || '手工收入', direction: '收入', amount: Number(inc.amount), cash_account_id: inc.account_id }),
      }).then(x => x.json());
      if (res.success) { setOpenIncome(false); setInc({ account_id: '', account_code: '4001', amount: '', date: today(), summary: '' }); afterMutate('已记一笔收入'); }
      else message.error(res.error || '记收入失败');
    } catch (e) { message.error('记收入失败：' + e.message); }
  };

  // ===== 记支出 =====
  const [exp, setExp] = useState({ account_id: '', account_code: '6099', amount: '', date: today(), summary: '', supplier_id: undefined, payable_id: undefined });
  const submitExpense = async () => {
    if (!exp.account_id) return message.warning('请选择付款账户');
    if (!exp.amount || Number(exp.amount) <= 0) return message.warning('请填写正数金额');
    try {
      const res = await apiFetch('/api/ledger/flow', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ biz_date: exp.date, source: '手工', entity_id: 1, account_code: exp.account_code, summary: exp.summary || '手工支出', direction: '支出', amount: Number(exp.amount), cash_account_id: exp.account_id, supplier_id: exp.supplier_id || null, payable_id: exp.payable_id || null }),
      }).then(x => x.json());
      if (res.success) { setOpenExpense(false); setExp({ account_id: '', account_code: '6099', amount: '', date: today(), summary: '', supplier_id: undefined, payable_id: undefined }); afterMutate('已记一笔支出'); }
      else message.error(res.error || '记支出失败');
    } catch (e) { message.error('记支出失败：' + e.message); }
  };

  // ===== 转账 =====
  const [tf, setTf] = useState({ from_id: '', to_id: '', amount: '', date: today(), summary: '' });
  const submitTransfer = async () => {
    if (!tf.from_id || !tf.to_id) return message.warning('请选择转出/转入账户');
    if (tf.from_id === tf.to_id) return message.warning('转出与转入账户不能相同');
    if (!tf.amount || Number(tf.amount) <= 0) return message.warning('请填写正数金额');
    try {
      const res = await apiFetch('/api/cash/transfer', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_id: tf.from_id, to_id: tf.to_id, amount: Number(tf.amount), biz_date: tf.date, summary: tf.summary }),
      }).then(x => x.json());
      if (res.success) { setOpenTransfer(false); setTf({ from_id: '', to_id: '', amount: '', date: today(), summary: '' }); afterMutate('转账成功'); }
      else message.error(res.error || '转账失败');
    } catch (e) { message.error('转账失败：' + e.message); }
  };

  // ===== 报销 =====
  const [rb, setRb] = useState({ applicant: '', reason: '', amount: '', account_code: '6099', cash_account_id: '', date: today(), remark: '' });
  const submitReimb = async () => {
    if (!rb.applicant) return message.warning('请填写报销人');
    if (!rb.reason) return message.warning('请填写报销事由');
    if (!rb.amount || Number(rb.amount) <= 0) return message.warning('请填写正数金额');
    if (!rb.cash_account_id) return message.warning('请选择付款账户');
    try {
      const res = await apiFetch('/api/reimbursement', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicant: rb.applicant, reason: rb.reason, amount: Number(rb.amount), account_code: rb.account_code, cash_account_id: rb.cash_account_id, biz_date: rb.date, remark: rb.remark }),
      }).then(x => x.json());
      if (res.success) { setOpenReimb(false); setRb({ applicant: '', reason: '', amount: '', account_code: '6099', cash_account_id: '', date: today(), remark: '' }); afterMutate('已建报销单'); }
      else message.error(res.error || '建报销单失败');
    } catch (e) { message.error('建报销单失败：' + e.message); }
  };
  const payReimb = async (id) => {
    try {
      const res = await apiFetch('/api/reimbursement/' + id + '/pay', { method: 'POST' }).then(x => x.json());
      if (res.success) afterMutate('已核销报销，并生成一笔支出');
      else message.error(res.error || '核销失败');
    } catch (e) { message.error('核销失败：' + e.message); }
  };
  const delReimb = async (id) => {
    try {
      const res = await apiFetch('/api/reimbursement/' + id, { method: 'DELETE' }).then(x => x.json());
      if (res.success) afterMutate('已删除报销单');
      else message.error(res.error || '删除失败');
    } catch (e) { message.error('删除失败：' + e.message); }
  };

  // ===== 账户设置 =====
  const [acc, setAcc] = useState({ name: '', type: 'bank', opening_balance: '' });
  const submitAccount = async () => {
    if (!acc.name) return message.warning('请填写账户名');
    try {
      const body = { name: acc.name, type: acc.type, opening_balance: acc.opening_balance === '' ? 0 : Number(acc.opening_balance) };
      if (editAccount) body.id = editAccount.id;
      const res = await apiFetch('/api/cash-account', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      }).then(x => x.json());
      if (res.success) { setOpenAccount(false); setEditAccount(null); setAcc({ name: '', type: 'bank', opening_balance: '' }); afterMutate(editAccount ? '账户已保存' : '账户已新增'); }
      else message.error(res.error || '保存失败');
    } catch (e) { message.error('保存失败：' + e.message); }
  };
  const delAccount = async (id) => {
    try {
      const res = await apiFetch('/api/cash-account/' + id, { method: 'DELETE' }).then(x => x.json());
      if (res.success) afterMutate('账户已删除');
      else message.error(res.error || '删除失败');
    } catch (e) { message.error('删除失败：' + e.message); }
  };

  // ===== 导入账单：预览 + 确认导入 =====
  const doPreview = async () => {
    if (!imp.account_id) return message.warning('请先选择资金账户（这笔账单属于哪个账户）');
    if (!imp.file) return message.warning('请选择账单文件');
    setImp(s => ({ ...s, loading: true, preview: null }));
    try {
      const fd = new FormData();
      fd.append('file', imp.file);
      fd.append('account_id', imp.account_id);
      const res = await apiFetch('/api/cash/import-bill/preview', { method: 'POST', body: fd }).then(x => x.json());
      if (res.success) {
        // 内部资金转移（余额宝自动攒/转账红包等）默认勾选「跳过」，老板可手动取消
        res.rows = (res.rows || []).map(r => ({ ...r, _skip: !!r.internal }));
        setImp(s => ({ ...s, preview: res }));
      }
      else message.error(res.error || '解析失败');
    } catch (e) { message.error('解析失败：' + e.message); }
    setImp(s => ({ ...s, loading: false }));
  };
  const changeImpRow = (idx, patch) => {
    setImp(s => {
      if (!s.preview) return s;
      const rows = s.preview.rows.slice();
      rows[idx] = { ...rows[idx], ...patch };
      return { ...s, preview: { ...s.preview, rows } };
    });
  };
  const submitImport = async () => {
    if (!imp.preview) return;
    const rows = imp.preview.rows.filter(r => !r._skip);
    if (!rows.length) return message.warning('没有可导入的流水行（都被勾选了跳过）');
    setImp(s => ({ ...s, importing: true }));
    try {
      const res = await apiFetch('/api/cash/import-bill', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: imp.account_id, entity_id: 1, rows }),
      }).then(x => x.json());
      if (res.success) {
        message.success(`已导入 ${res.added} 笔` + (res.skipped ? `，跳过重复 ${res.skipped} 笔` : '') + (imp.preview.rows.length - rows.length ? `，手动跳过 ${imp.preview.rows.length - rows.length} 笔` : ''));
        setOpenImport(false);
        setImp({ account_id: '', file: null, loading: false, importing: false, preview: null });
        afterMutate('账单导入完成');
      } else message.error(res.error || '导入失败');
    } catch (e) { message.error('导入失败：' + e.message); }
    setImp(s => ({ ...s, importing: false }));
  };

  const manualAccounts = cash && cash.accounts ? cash.accounts : [];
  const shopAccounts = cash && cash.shops ? cash.shops : [];
  const totalBalance = cash ? cash.total : 0;
  const incomeOptions = cash && cash.income_accounts ? cash.income_accounts : [];
  const expenseOptions = cash && cash.expense_accounts ? cash.expense_accounts : [];
  const typeOptions = cash && cash.types ? cash.types : [];
  const transferOptions = [
    ...manualAccounts.map(a => ({ value: String(a.id), label: a.name, kind: 'manual' })),
    ...shopAccounts.map(s => ({ value: String(s.id), label: s.name, kind: 'shop' })),
  ];
  // 当前选中供应商名下的未付应付（关联核销下拉）
  const supplierPayables = unpaidPayables.filter(p => Number(p.supplier_id) === Number(exp.supplier_id));

  // ===== 面单单价设置 =====
  const [wbPrice, setWbPrice] = useState('');
  const saveWbPrice = async () => {
    const p = Number(wbPrice);
    if (!isFinite(p) || p <= 0) return message.warning('请输入正数单价');
    try {
      const res = await apiFetch('/api/waybill-unit-price', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ unit_price: p }),
      }).then(x => x.json());
      if (res.success) { setWaybill(res.data); setWbPrice(''); message.success('面单单价已更新'); }
      else message.error(res.error || '更新失败');
    } catch (e) { message.error('更新失败：' + e.message); }
  };

  // ===== 余额总览 Tab =====
  // 本期净流入（本月收入 − 支出，转账为内部流转不计入）
  const thisMonth = dayjs().format('YYYY-MM');
  const netInflow = round2((flow || []).reduce((s, f) => s + (String(f.date || '').startsWith(thisMonth) ? (f.type === '收入' ? Number(f.amount) : f.type === '支出' ? -Number(f.amount) : 0) : 0), 0));
  const [intPart, fracPart] = fmtFull(totalBalance).split('.');
  const totalPositive = manualAccounts.reduce((s, a) => s + Math.max(0, Number(a.balance) || 0), 0);
  // 玻璃拟态公共样式（与全站「一层 0.42 色调1」统一）
  const glass = {
    background: 'rgba(255,255,255,0.42)',
    backdropFilter: 'blur(18px) saturate(1.7)', WebkitBackdropFilter: 'blur(18px) saturate(1.7)',
    border: '1px solid rgba(255,255,255,0.72)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.92), 0 6px 20px rgba(216,90,48,0.07)',
  };
  const coralIco = { background: 'linear-gradient(135deg, #D85A30, #F08A62)', color: '#fff', boxShadow: '0 4px 10px rgba(216,90,48,0.24)' };

  const overviewTab = (
    <div>
      {/* 资金总余额横幅：浅色玻璃 + 珊瑚光晕 */}
      <div style={{
        position: 'relative', overflow: 'hidden', borderRadius: 20, padding: '26px 28px', marginBottom: 18,
        ...glass, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.95), 0 14px 40px rgba(216,90,48,0.14)',
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(115deg, rgba(216,90,48,0.13) 0%, rgba(232,116,78,0.05) 48%, rgba(240,138,98,0) 75%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', right: -70, top: -70, width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(240,138,98,0.32) 0%, rgba(240,138,98,0) 70%)', filter: 'blur(18px)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 13, color: '#5F5E5A', display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: CORAL, boxShadow: '0 0 0 3px rgba(216,90,48,0.16)' }} />
            资金总余额（银行卡 + 微信 + 支付宝 + 现金）
          </div>
          <div style={{ fontSize: 48, fontWeight: 700, marginTop: 8, letterSpacing: .5, lineHeight: 1.04, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif', color: INK }}>
            ¥ {intPart}<span style={{ fontSize: 24, fontWeight: 600, color: CORAL }}>.{fracPart}</span>
          </div>
          <div style={{ display: 'flex', gap: 30, marginTop: 18, paddingTop: 16, borderTop: '1px solid rgba(216,90,48,0.12)', flexWrap: 'wrap' }}>
            <div><div style={{ fontSize: 12, color: '#A08C82' }}>资金账户</div><div style={{ fontSize: 17, color: INK, fontWeight: 600, marginTop: 3, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{manualAccounts.length} 个</div></div>
            <div><div style={{ fontSize: 12, color: '#A08C82' }}>店铺账户</div><div style={{ fontSize: 17, color: INK, fontWeight: 600, marginTop: 3, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{shopAccounts.length} 个</div></div>
            <div><div style={{ fontSize: 12, color: '#A08C82' }}>待报销</div><div style={{ fontSize: 17, color: INK, fontWeight: 600, marginTop: 3, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{cash ? cash.reimbursement_pending : 0} 笔 · {fmtYuan(cash ? cash.reimbursement_pending_amount : 0)}</div></div>
            <div><div style={{ fontSize: 12, color: '#A08C82' }}>本期净流入</div><div style={{ fontSize: 17, fontWeight: 600, marginTop: 3, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif', color: netInflow >= 0 ? CORAL : '#3F8600' }}>{netInflow >= 0 ? '+' : '-'}¥ {fmtFull(Math.abs(netInflow))}</div></div>
          </div>
        </div>
      </div>

      {/* 账户卡片自适应网格（统一珊瑚色 + 占比条） */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 13, marginBottom: 18 }}>
        {manualAccounts.map(a => {
          const meta = TYPE_META[a.type] || TYPE_META.cash;
          const neg = Number(a.balance) < 0;
          const pct = totalPositive > 0 ? Math.min(100, Math.round(Math.max(0, Number(a.balance) || 0) / totalPositive * 100)) : 0;
          return (
            <div key={a.id} className="kpi-tap" style={{
              flex: '1 1 180px', maxWidth: 'calc(25% - 10px)', minWidth: 170, borderRadius: 16, padding: '16px', overflow: 'hidden',
              ...glass, transition: 'transform .08s ease, box-shadow .2s ease', cursor: 'default',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, flex: 'none', ...coralIco }}>{TYPE_CHAR[a.type] || '账'}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                  <div style={{ fontSize: 10.5, color: '#A08C82' }}>{meta.label}</div>
                </div>
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: neg ? '#CF1322' : INK, marginTop: 14, letterSpacing: .3, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{neg ? '-¥ ' : '¥ '}{fmtFull(Math.abs(a.balance))}</div>
              <div style={{ height: 4, borderRadius: 99, background: 'rgba(216,90,48,0.12)', marginTop: 13, overflow: 'hidden' }}>
                <div style={{ width: pct + '%', height: '100%', borderRadius: 99, background: 'linear-gradient(90deg, #D85A30, #F08A62)' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 7, fontSize: 11, color: '#A08C82' }}>
                <span>期初 ¥{fmtFull(a.opening_balance)}</span>
                <span style={{ fontWeight: 600, color: CORAL }}>{pct}%</span>
              </div>
            </div>
          );
        })}
        {manualAccounts.length === 0 && <div style={{ width: '100%' }}><Empty description="暂无资金账户，请到「账户设置」新增" /></div>}
      </div>

      {/* 底部两卡：待报销提醒 + 店铺账户 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
        <div style={{ borderRadius: 16, padding: '16px 18px', ...glass }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>
            <span style={{ width: 26, height: 26, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flex: 'none', ...coralIco }}><FileDoneOutlined /></span>待报销提醒
          </div>
          {cash && cash.reimbursement_pending > 0 ? (
            <div style={{ fontSize: 13.5, color: '#D48806', lineHeight: 1.8 }}>
              有 <b style={{ fontSize: 15 }}>{cash.reimbursement_pending}</b> 笔报销待处理，合计 <b style={{ fontSize: 15 }}>{fmtYuan(cash.reimbursement_pending_amount)}</b>。<br />
              这些是「已承诺但还没出账」的垫付，核销后会自动生成支出并扣对应账户余额。
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#A08C82' }}>暂无待报销单。</div>
          )}
        </div>
        <div style={{ borderRadius: 16, padding: '16px 18px', ...glass }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>
            <span style={{ width: 26, height: 26, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flex: 'none', ...coralIco }}><ShopOutlined /></span>店铺账户（提现来源 / 去向）
          </div>
          <div style={{ fontSize: 12, color: '#A08C82', lineHeight: 1.7, marginBottom: 8 }}>
            店铺账户是拼多多平台的待结算资金，系统不重复维护余额（以拼多多后台为准）。提现时记一笔「店铺 → 银行卡」转账即可。
          </div>
          <Space wrap size={6}>
            {shopAccounts.map(s => <Tag key={s.id} style={{ borderRadius: 999, padding: '2px 11px', background: 'rgba(255,248,244,0.7)', borderColor: '#F3E2D8', color: '#8C5B44' }}>{s.name}</Tag>)}
            {shopAccounts.length === 0 && <span style={{ fontSize: 12, color: '#A08C82' }}>暂无店铺（在「账务流水 → 店铺映射」登记）</span>}
          </Space>
        </div>
      </div>
      {/* 面单余额核对（预付快递费 2002） */}
      <div style={{ borderRadius: 16, padding: '18px 20px', marginTop: 13, ...glass }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: INK }}>
            <span style={{ width: 26, height: 26, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flex: 'none', ...coralIco }}><InboxOutlined /></span>
            面单余额核对（预付快递费）
            <span style={{ fontSize: 11, fontWeight: 400, color: '#A08C82' }}>自 {waybill ? waybill.start_month : ''} 起算 · 消耗按发货订单数（排除代发）</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: '#A08C82' }}>单价 ¥</span>
            <InputNumber size="small" min={0.01} precision={2} style={{ width: 74 }}
              value={wbPrice === '' ? null : wbPrice} onChange={v => setWbPrice(v == null ? '' : v)}
              placeholder={waybill ? String(waybill.unit_price) : '3'} />
            <span style={{ fontSize: 12, color: '#A08C82' }}>/个</span>
            <Button size="small" type="primary" ghost onClick={saveWbPrice}>更新单价</Button>
          </div>
        </div>
        {waybill ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#A08C82' }}>累计充值</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: INK, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif', marginTop: 2 }}>{fmtYuan(waybill.total_recharge)}</div>
              <div style={{ fontSize: 11, color: '#A08C82', marginTop: 2 }}>≈ {fmtFull(waybill.recharge_count)} 个号</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#A08C82' }}>已消耗（发货）</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#3F8600', fontFamily: 'DIN Alternate, "Segoe UI", sans-serif', marginTop: 2 }}>{waybill.used_count} 单</div>
              <div style={{ fontSize: 11, color: '#A08C82', marginTop: 2 }}>代发 {waybill.dropship_count} 单不计</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#A08C82' }}>面单余额</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: waybill.balance_count >= 0 ? CORAL : '#CF1322', fontFamily: 'DIN Alternate, "Segoe UI", sans-serif', marginTop: 2 }}>
                {fmtFull(waybill.balance_count)} 个
              </div>
              <div style={{ fontSize: 11, color: '#A08C82', marginTop: 2 }}>{fmtYuan(waybill.balance_amount)}</div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: '#A08C82' }}>加载中…</div>
        )}
        {waybill && Array.isArray(waybill.recharge_rows) && waybill.recharge_rows.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: INK, marginBottom: 8 }}>充值明细</div>
            <div style={{ borderRadius: 10, border: '1px solid rgba(216,90,48,0.14)', overflow: 'hidden' }}>
              {waybill.recharge_rows.map((r, i) => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', fontSize: 12, background: i % 2 === 0 ? 'rgba(255,248,244,0.5)' : 'transparent', borderTop: i > 0 ? '1px solid rgba(216,90,48,0.08)' : 'none' }}>
                  <span style={{ color: '#A08C82', flex: 'none', width: 84 }}>{r.biz_date}</span>
                  <Tag style={{ borderRadius: 999, margin: 0, flex: 'none' }}>{r.account_name}</Tag>
                  <span style={{ color: INK, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.summary || '面单充值'}</span>
                  <span style={{ color: '#CF1322', fontWeight: 600, flex: 'none', fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>−{fmtYuan(r.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ fontSize: 11, color: '#A08C82', marginTop: 12, lineHeight: 1.7, borderTop: '1px dashed rgba(216,90,48,0.14)', paddingTop: 10 }}>
          面单充值（2002 科目）是预付，充值时钱变成面单号余额，不直接进净利；发货消耗面单号才产生真实运费。余额为负 = 已发超充，需补充值；为正 = 还有面单号没用完。
        </div>
      </div>
    </div>
  );

  // ===== 资金流水 Tab =====
  const flowColumns = [
    { title: '日期', dataIndex: 'date', width: 110, render: v => <span style={{ fontSize: 12 }}>{v}</span> },
    {
      title: '类型', dataIndex: 'type', width: 80,
      render: t => t === '转账' ? <Tag color="blue" style={{ borderRadius: 999 }}>转账</Tag> : t === '收入' ? <Tag color="green" style={{ borderRadius: 999 }}>收入</Tag> : <Tag color="red" style={{ borderRadius: 999 }}>支出</Tag>,
    },
    { title: '摘要', dataIndex: 'summary', ellipsis: true },
    {
      title: '支付方式', dataIndex: 'pay_method', width: 86,
      render: v => v === '余额' ? <Tag color="green" style={{ borderRadius: 999, fontSize: 11, lineHeight: '16px', margin: 0 }}>余额</Tag> : v === '银行卡' ? <Tag color="blue" style={{ borderRadius: 999, fontSize: 11, lineHeight: '16px', margin: 0 }}>银行卡</Tag> : (v ? <Tag style={{ borderRadius: 999, fontSize: 11, lineHeight: '16px', margin: 0 }}>{v}</Tag> : <span style={{ color: '#bfbfbf' }}>—</span>),
    },
    {
      title: '收款方', dataIndex: 'supplier_name', width: 160,
      render: (v, r) => r.supplier_name ? (
        <span style={{ fontSize: 12 }}>{r.supplier_name}{r.payable_no ? <Tag color="orange" style={{ borderRadius: 999, marginLeft: 6, fontSize: 11, lineHeight: '16px' }}>{r.payable_no}</Tag> : null}</span>
      ) : <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: '账户（出 → 入）', dataIndex: 'from_account', width: 220,
      render: (v, r) => r.type === '转账' ? <span style={{ fontSize: 12 }}>{r.from_account} <span style={{ color: '#A08C82' }}>→</span> {r.to_account}</span> : <span style={{ fontSize: 12 }}>{r.from_account || '—'}</span>,
    },
    {
      title: '金额', dataIndex: 'amount', width: 130, align: 'right',
      render: (v, r) => <span style={{ fontWeight: 600, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif', color: r.type === '收入' ? '#3F8600' : r.type === '转账' ? '#5B8FF9' : '#CF1322' }}>{r.type === '收入' ? '+' : r.type === '支出' ? '-' : ''}{fmtYuan(v)}</span>,
    },
  ];
  // ===== 流水查找（原「刷新」按钮改造）：点「查找」按 账户/日期区间/关键字 真正过滤 =====
  const applyFilter = () => {
    const account = (fAccount || '').trim();
    const start = (fStart || '').trim();
    const end = (fEnd || '').trim();
    const key = (fKey || '').trim();
    if (!account && !start && !end && !key) { setApplied(null); return; }
    setApplied({ account, start, end, key });
  };
  const clearFilter = () => { setApplied(null); setFAccount(''); setFStart(''); setFEnd(''); setFKey(''); };
  const flowFiltered = useMemo(() => {
    if (!applied) return flow || [];
    const { account, start, end, key } = applied;
    return (flow || []).filter(f => {
      if (account) {
        const from = String(f.from_account || '');
        const to = String(f.to_account || '');
        // 双向包含：兼容店铺账户「xx（店铺）」等后缀差异
        if (!(from.includes(account) || to.includes(account) || account.includes(from) || account.includes(to))) return false;
      }
      const d = String(f.date || '');
      if (start && d && d < start) return false;
      if (end && d && d > end) return false;
      if (key) {
        const hay = String(f.summary || '') + ' ' + String(f.supplier_name || '') + ' ' + String(f.payable_no || '');
        if (!hay.includes(key)) return false;
      }
      return true;
    });
  }, [flow, applied]);

  const flowTab = (
    <div>
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12,
        background: '#fff', border: '1px solid #F3E2D8', borderRadius: 12, padding: '10px 12px',
      }}>
        <Select placeholder="全部账户" allowClear style={{ width: 150 }} value={fAccount || undefined} onChange={setFAccount}
          options={[...manualAccounts.map(a => ({ value: a.name, label: a.name })), ...shopAccounts.map(s => ({ value: s.name.replace('（店铺）', ''), label: s.name }))]} />
        <DatePicker placeholder="开始日期" style={{ width: 140 }} value={fStart ? dayjs(fStart) : null} onChange={d => setFStart(d ? d.format('YYYY-MM-DD') : '')} format="YYYY-MM-DD" allowClear />
        <DatePicker placeholder="结束日期" style={{ width: 140 }} value={fEnd ? dayjs(fEnd) : null} onChange={d => setFEnd(d ? d.format('YYYY-MM-DD') : '')} format="YYYY-MM-DD" allowClear />
        <Input placeholder="摘要 / 收款方关键字" style={{ width: 165 }} value={fKey} onChange={e => setFKey(e.target.value)} onPressEnter={applyFilter} allowClear prefix={<SearchOutlined style={{ color: '#C9B8AC' }} />} />
        <Button type="primary" ghost icon={<SearchOutlined />} onClick={applyFilter}>查找</Button>
        {applied && <Button type="link" style={{ padding: '0 4px' }} onClick={clearFilter}>清除筛选</Button>}
        {applied && <span style={{ fontSize: 12, color: '#A08C82' }}>已筛出 {flowFiltered.length} / {(flow || []).length} 条</span>}
        <div style={{ flex: 1, minWidth: 8 }} />
        <Button type="primary" icon={<ArrowDownOutlined />} onClick={() => setOpenIncome(true)} style={{ background: '#3F8600' }}>记收入</Button>
        <Button danger icon={<ArrowUpOutlined />} onClick={() => setOpenExpense(true)}>记支出</Button>
        <Button icon={<SwapOutlined />} onClick={() => setOpenTransfer(true)}>转账</Button>
        <Button icon={<ImportOutlined />} onClick={() => { setImp({ account_id: '', file: null, loading: false, importing: false, preview: null }); setOpenImport(true); }}>导入账单</Button>
      </div>
      <Table
        rowKey="id" size="small" dataSource={flowFiltered} columns={flowColumns} pagination={{ pageSize: 20, showSizeChanger: false }}
        locale={{ emptyText: applied ? '没有符合条件的流水，试试放宽筛选条件或点「清除筛选」。' : '暂无资金流水。提现/收支/报销核销后会自动出现在这里。' }}
      />
    </div>
  );

  // ===== 报销 Tab =====
  const accNameOf = (id) => {
    const a = manualAccounts.find(x => String(x.id) === String(id));
    return a ? a.name : (id || '');
  };
  const codeName = (code) => {
    const e = expenseOptions.find(x => x.code === code);
    return e ? e.name : code;
  };
  const reimbColumns = [
    { title: '报销人', dataIndex: 'applicant', width: 100 },
    { title: '事由', dataIndex: 'reason', ellipsis: true },
    { title: '金额', dataIndex: 'amount', width: 110, align: 'right', render: v => <span style={{ fontWeight: 600, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(v)}</span> },
    { title: '费用科目', dataIndex: 'account_code', width: 110, render: v => codeName(v) },
    { title: '付款账户', dataIndex: 'cash_account_id', width: 110, render: v => accNameOf(v) },
    { title: '日期', dataIndex: 'biz_date', width: 110, render: v => <span style={{ fontSize: 12 }}>{v}</span> },
    {
      title: '状态', dataIndex: 'status', width: 90,
      render: s => s === '已报销' ? <Tag color="success" style={{ borderRadius: 999 }}>已报销</Tag> : <Tag color="warning" style={{ borderRadius: 999 }}>待报销</Tag>,
    },
    {
      title: '操作', width: 150, align: 'center',
      render: (_, r) => r.status === '待报销' ? (
        <Space size={4}>
          <Button size="small" type="primary" onClick={() => payReimb(r.id)}>核销</Button>
          <Popconfirm title="删除这笔报销单？" onConfirm={() => delReimb(r.id)}><Button size="small" danger>删</Button></Popconfirm>
        </Space>
      ) : <span style={{ fontSize: 12, color: '#A08C82' }}>{r.reimbursed_at ? String(r.reimbursed_at).slice(0, 10) : ''}</span>,
    },
  ];
  const reimbTab = (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpenReimb(true)}>新建报销</Button>
        <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
      </Space>
      <Table
        rowKey="id" size="small" dataSource={reimb} columns={reimbColumns} pagination={{ pageSize: 20, showSizeChanger: false }}
        locale={{ emptyText: '暂无报销单。点击「新建报销」登记垫付/支出。' }}
      />
    </div>
  );

  // ===== 账户设置 Tab =====
  const accountColumns = [
    { title: '账户名', dataIndex: 'name' },
    { title: '类型', dataIndex: 'type', width: 110, render: t => { const m = TYPE_META[t] || TYPE_META.cash; return <span style={{ color: m.color }}>{m.icon} {m.label}</span>; } },
    { title: '期初余额', dataIndex: 'opening_balance', width: 130, align: 'right', render: v => fmtYuan(v) },
    { title: '当前余额', dataIndex: 'balance', width: 130, align: 'right', render: v => <b style={{ fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(v)}</b> },
    {
      title: '操作', width: 140, align: 'center',
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" onClick={() => { setEditAccount(r); setAcc({ name: r.name, type: r.type, opening_balance: r.opening_balance }); setOpenAccount(true); }}>编辑</Button>
          <Popconfirm title="删除该账户？（有资金流水的账户不能删）" onConfirm={() => delAccount(r.id)}><Button size="small" danger>删</Button></Popconfirm>
        </Space>
      ),
    },
  ];
  const accountTab = (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditAccount(null); setAcc({ name: '', type: 'bank', opening_balance: '' }); setOpenAccount(true); }}>新增账户</Button>
        <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
      </Space>
      <div style={{ fontSize: 12, color: '#A08C82', marginBottom: 8, lineHeight: 1.7 }}>
        期初余额 = 启用资金账那一刻该账户的真实余额，填一次即可。之后每天的收支/转账会自动滚动余额。店铺账户从「账务流水 → 店铺映射」自动派生，无需在这里建。
      </div>
      <Table rowKey="id" size="small" dataSource={manualAccounts} columns={accountColumns} pagination={false} />
    </div>
  );

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: INK }}>
            <DollarOutlined style={{ color: CORAL, marginRight: 8 }} />资金中心
          </div>
          <div style={{ fontSize: 12, color: '#A08C82', marginTop: 4 }}>
            钱在哪个账户、还剩多少、每天花到哪　|　提现 = 店铺 → 银行卡转账；报销核销自动出账扣余额
          </div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
      </div>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          { key: 'overview', label: <span><BankOutlined /> 余额总览</span>, children: tab === 'overview' ? overviewTab : null },
          { key: 'flow', label: <span><SwapOutlined /> 资金流水</span>, children: tab === 'flow' ? flowTab : null },
          { key: 'reimb', label: <span><FileDoneOutlined /> 报销</span>, children: tab === 'reimb' ? reimbTab : null },
          { key: 'setting', label: <span><SettingOutlined /> 账户设置</span>, children: tab === 'setting' ? accountTab : null },
        ]}
      />

      {/* 记收入 */}
      <Modal title="记一笔收入" open={openIncome} onCancel={() => setOpenIncome(false)} onOk={submitIncome} okText="保存" cancelText="取消" destroyOnClose>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>收款账户（钱进到哪）</div>
            <Select style={{ width: '100%' }} placeholder="选择账户" value={inc.account_id || undefined} onChange={v => setInc({ ...inc, account_id: v })}
              options={manualAccounts.map(a => ({ value: String(a.id), label: a.name }))} />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>收入科目</div>
            <Select style={{ width: '100%' }} value={inc.account_code} onChange={v => setInc({ ...inc, account_code: v })}
              options={incomeOptions.map(a => ({ value: a.code, label: a.name + '（' + a.code + '）' }))} />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>金额（元）</div>
            <InputNumber style={{ width: '100%' }} min={0} precision={2} value={inc.amount || null} onChange={v => setInc({ ...inc, amount: v })} placeholder="如 10000" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>日期</div>
            <Input value={inc.date} onChange={e => setInc({ ...inc, date: e.target.value })} placeholder="YYYY-MM-DD" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>摘要</div>
            <Input value={inc.summary} onChange={e => setInc({ ...inc, summary: e.target.value })} placeholder="如：老板投入 / 其他收入" />
          </div>
        </Space>
      </Modal>

      {/* 记支出 */}
      <Modal title="记一笔支出" open={openExpense} onCancel={() => setOpenExpense(false)} onOk={submitExpense} okText="保存" cancelText="取消" destroyOnClose>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>付款账户（钱从哪出）</div>
            <Select style={{ width: '100%' }} placeholder="选择账户" value={exp.account_id || undefined} onChange={v => setExp({ ...exp, account_id: v })}
              options={manualAccounts.map(a => ({ value: String(a.id), label: a.name }))} />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>支出科目</div>
            <Select style={{ width: '100%' }} value={exp.account_code} onChange={v => setExp({ ...exp, account_code: v })}
              options={expenseOptions.map(a => ({ value: a.code, label: a.name + '（' + a.code + '）' }))} />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>收款方（供应商，可选）</div>
            <Select style={{ width: '100%' }} allowClear showSearch optionFilterProp="label" placeholder="选填：付给哪家供应商"
              value={exp.supplier_id || undefined}
              onChange={v => setExp({ ...exp, supplier_id: v, payable_id: undefined })}
              options={suppliers.map(s => ({ value: s.id, label: (s.code ? s.code + ' ' : '') + s.name }))} />
          </div>
          {exp.supplier_id && supplierPayables.length > 0 && (
            <div>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>关联应付账款（付款后自动核销）</div>
              <Select style={{ width: '100%' }} allowClear placeholder="选一条未付应付，保存后自动核销"
                value={exp.payable_id || undefined}
                onChange={v => setExp({ ...exp, payable_id: v })}
                options={supplierPayables.map(p => ({ value: p.id, label: (p.ap_no || ('应付#' + p.id)) + ' · 剩余 ¥' + fmtYuan(p.remaining) }))} />
              <div style={{ fontSize: 12, color: '#A08C82', marginTop: 4, lineHeight: 1.6 }}>
                选后保存会自动核销该应付：付款金额 ≤ 剩余未付，付满即自动转「已付」，不足则扣减剩余。
              </div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>金额（元）</div>
            <InputNumber style={{ width: '100%' }} min={0} precision={2} value={exp.amount || null} onChange={v => setExp({ ...exp, amount: v })} placeholder="如 5000" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>日期</div>
            <Input value={exp.date} onChange={e => setExp({ ...exp, date: e.target.value })} placeholder="YYYY-MM-DD" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>摘要</div>
            <Input value={exp.summary} onChange={e => setExp({ ...exp, summary: e.target.value })} placeholder="如：采购付款 / 房租" />
          </div>
        </Space>
      </Modal>

      {/* 转账 */}
      <Modal title="账户转账（提现 = 店铺 → 银行卡）" open={openTransfer} onCancel={() => setOpenTransfer(false)} onOk={submitTransfer} okText="确认转账" cancelText="取消" destroyOnClose>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>转出账户</div>
            <Select style={{ width: '100%' }} placeholder="选择转出账户" value={tf.from_id || undefined} onChange={v => setTf({ ...tf, from_id: v })}
              options={transferOptions.map(a => ({ value: a.value, label: a.label }))} />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>转入账户</div>
            <Select style={{ width: '100%' }} placeholder="选择转入账户" value={tf.to_id || undefined} onChange={v => setTf({ ...tf, to_id: v })}
              options={transferOptions.map(a => ({ value: a.value, label: a.label }))} />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>金额（元）</div>
            <InputNumber style={{ width: '100%' }} min={0} precision={2} value={tf.amount || null} onChange={v => setTf({ ...tf, amount: v })} placeholder="如 100000" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>日期</div>
            <Input value={tf.date} onChange={e => setTf({ ...tf, date: e.target.value })} placeholder="YYYY-MM-DD" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>摘要（可选）</div>
            <Input value={tf.summary} onChange={e => setTf({ ...tf, summary: e.target.value })} placeholder="如：拼多多提现到银行卡" />
          </div>
          <div style={{ fontSize: 12, color: '#A08C82', lineHeight: 1.6 }}>
            转账只在账户之间移动资金，<b>不产生损益</b>，不影响公司看板/财务健康的任何数字。
          </div>
        </Space>
      </Modal>

      {/* 新建报销 */}
      <Modal title="新建报销单" open={openReimb} onCancel={() => setOpenReimb(false)} onOk={submitReimb} okText="保存" cancelText="取消" destroyOnClose>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>报销人</div>
            <Input value={rb.applicant} onChange={e => setRb({ ...rb, applicant: e.target.value })} placeholder="谁垫付的" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>报销事由</div>
            <Input value={rb.reason} onChange={e => setRb({ ...rb, reason: e.target.value })} placeholder="花在哪了" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>金额（元）</div>
            <InputNumber style={{ width: '100%' }} min={0} precision={2} value={rb.amount || null} onChange={v => setRb({ ...rb, amount: v })} placeholder="如 350" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>费用科目</div>
            <Select style={{ width: '100%' }} value={rb.account_code} onChange={v => setRb({ ...rb, account_code: v })}
              options={expenseOptions.map(a => ({ value: a.code, label: a.name + '（' + a.code + '）' }))} />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>付款账户（核销时钱从哪出）</div>
            <Select style={{ width: '100%' }} placeholder="选择账户" value={rb.cash_account_id || undefined} onChange={v => setRb({ ...rb, cash_account_id: v })}
              options={manualAccounts.map(a => ({ value: String(a.id), label: a.name }))} />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>日期</div>
            <Input value={rb.date} onChange={e => setRb({ ...rb, date: e.target.value })} placeholder="YYYY-MM-DD" />
          </div>
          <div style={{ fontSize: 12, color: '#A08C82', lineHeight: 1.6 }}>
            报销单保存后是「待报销」状态；点「核销」会<b>自动生成一笔支出流水并扣对应账户余额</b>。
          </div>
        </Space>
      </Modal>

      {/* 账户设置 */}
      <Modal title={editAccount ? '编辑账户' : '新增账户'} open={openAccount} onCancel={() => { setOpenAccount(false); setEditAccount(null); }} onOk={submitAccount} okText="保存" cancelText="取消" destroyOnClose>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>账户名</div>
            <Input value={acc.name} onChange={e => setAcc({ ...acc, name: e.target.value })} placeholder="如：招行储蓄卡" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>类型</div>
            <Select style={{ width: '100%' }} value={acc.type} onChange={v => setAcc({ ...acc, type: v })}
              options={typeOptions.map(t => ({ value: t.value, label: t.label }))} />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>期初余额（元，启用日真实余额）</div>
            <InputNumber style={{ width: '100%' }} precision={2} value={acc.opening_balance === '' ? null : acc.opening_balance} onChange={v => setAcc({ ...acc, opening_balance: v == null ? '' : v })} placeholder="如 30000" />
          </div>
        </Space>
      </Modal>

      {/* 导入账单 */}
      <Modal
        title="导入账单（支付宝 / 微信 / 银行卡）"
        open={openImport}
        onCancel={() => { setOpenImport(false); setImp({ account_id: '', file: null, loading: false, importing: false, preview: null }); }}
        footer={imp.preview ? [
          <Button key="back" onClick={() => setImp(s => ({ ...s, preview: null }))}>重新上传</Button>,
          <Button key="ok" type="primary" loading={imp.importing} onClick={submitImport}>确认导入 {imp.preview ? imp.preview.rows.filter(r => !r._skip).length : 0} 笔</Button>,
        ] : [
          <Button key="ok" type="primary" loading={imp.loading} onClick={doPreview}>解析预览</Button>,
        ]}
        width={imp.preview ? 920 : 560}
        destroyOnClose
      >
        {!imp.preview ? (
          <Space direction="vertical" style={{ width: '100%' }} size={14}>
            <div>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 5 }}>资金账户（这笔账单属于哪个账户）</div>
              <Select style={{ width: '100%' }} placeholder="选择账户" value={imp.account_id || undefined}
                onChange={v => setImp({ ...imp, account_id: v })}
                options={manualAccounts.map(a => ({ value: String(a.id), label: a.name + '（' + (TYPE_META[a.type] ? TYPE_META[a.type].label : a.type) + '）' }))} />
            </div>
            <Upload.Dragger
              accept=".csv,.xls,.xlsx,.txt"
              maxCount={1}
              beforeUpload={(f) => { setImp(s => ({ ...s, file: f })); return false; }}
              onRemove={() => setImp(s => ({ ...s, file: null }))}
              fileList={imp.file ? [{ uid: '-1', name: imp.file.name }] : []}
              style={{ padding: '16px 0' }}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined style={{ color: '#D85A30' }} /></p>
              <p className="ant-upload-text" style={{ color: '#5F5E5A' }}>点击或拖拽账单文件到这里</p>
              <p className="ant-upload-hint" style={{ color: '#A08C82' }}>
                支持支付宝 / 微信 / 银行卡导出的 CSV 或 Excel；自动识别收入/支出并分类，支出自动标注「余额 / 银行卡」支付方式
              </p>
            </Upload.Dragger>
            <Alert type="info" showIcon style={{ borderRadius: 10 }}
              message="导入前须知"
              description="系统会按账单里的交易时间 / 金额 / 收支 / 交易对方 / 商品说明自动分类（支出归到运费、推广、采购等科目，收入归到销售收入）。预览阶段每笔的科目都能改，确认无误再导入。重复导入同一份账单会自动跳过重复笔。"
            />
          </Space>
        ) : (
          <div>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#5F5E5A' }}>识别来源：<b style={{ color: '#D85A30' }}>{imp.preview.platform}</b></span>
              <span style={{ fontSize: 13, color: '#5F5E5A' }}>共 <b>{imp.preview.total}</b> 笔</span>
              <span style={{ fontSize: 13, color: '#3F8600' }}>收入 <b>{imp.preview.income_cnt}</b> 笔 · {fmtYuan(imp.preview.income_amt)}</span>
              <span style={{ fontSize: 13, color: '#CF1322' }}>支出 <b>{imp.preview.expense_cnt}</b> 笔 · {fmtYuan(imp.preview.expense_amt)}</span>
              {imp.preview.skipped > 0 && <span style={{ fontSize: 13, color: '#A08C82' }}>已跳过 {imp.preview.skipped} 笔（不计收支/内部转账）</span>}
            </div>
            <div style={{ fontSize: 12, color: '#A08C82', marginBottom: 8 }}>
              科目可下拉调整；「跳过」默认勾掉了余额宝自动攒/转账红包等内部资金转移，个人消费（如购物、加油）请手动勾选跳过。确认后写入该账户的资金流水。
            </div>
            <Table
              rowKey={(r, i) => String(i)}
              size="small"
              dataSource={imp.preview.rows}
              pagination={{ pageSize: 50, showSizeChanger: false }}
              scroll={{ y: 420 }}
              columns={[
                { title: '日期', dataIndex: 'biz_date', width: 100, render: v => <span style={{ fontSize: 12 }}>{v}</span> },
                { title: '收支', dataIndex: 'direction', width: 66, render: t => t === '收入' ? <Tag color="green" style={{ borderRadius: 999, margin: 0 }}>收入</Tag> : <Tag color="red" style={{ borderRadius: 999, margin: 0 }}>支出</Tag> },
                {
                  title: '交易对方 / 说明', dataIndex: 'summary', ellipsis: true,
                  render: (v, r) => (
                    <span style={{ fontSize: 12 }}>
                      {r.trans_type ? <Tag style={{ borderRadius: 999, marginRight: 6, fontSize: 11, lineHeight: '16px', color: '#8C5B44', background: 'rgba(216,90,48,0.08)', borderColor: '#F3E2D8' }}>{r.trans_type}</Tag> : null}
                      {v}
                      {r.internal ? <Tag color="orange" style={{ borderRadius: 999, marginLeft: 6, fontSize: 11, lineHeight: '16px' }}>内部</Tag> : null}
                      {r.pay_method ? <Tag color="blue" style={{ borderRadius: 999, marginLeft: 6, fontSize: 11, lineHeight: '16px' }}>{r.pay_method}</Tag> : null}
                    </span>
                  ),
                },
                {
                  title: '科目（可改）', dataIndex: 'account_code', width: 200,
                  render: (v, r, i) => {
                    const opts = r.direction === '收入' ? (imp.preview.income_accounts || []) : (imp.preview.expense_accounts || []);
                    return <Select size="small" style={{ width: 190 }} value={v} onChange={val => changeImpRow(i, { account_code: val })}
                      options={opts.map(a => ({ value: a.code, label: a.name + '（' + a.code + '）' }))} />;
                  },
                },
                { title: '金额', dataIndex: 'amount', width: 100, align: 'right', render: (v, r) => <span style={{ fontWeight: 600, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif', color: r.direction === '收入' ? '#3F8600' : '#CF1322' }}>{r.direction === '收入' ? '+' : '-'}{fmtYuan(v)}</span> },
                {
                  title: '跳过', dataIndex: '_skip', width: 58, align: 'center',
                  render: (v, r, i) => <Checkbox checked={!!v} onChange={e => changeImpRow(i, { _skip: e.target.checked })} />,
                },
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
